import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import html2canvas from 'html2canvas';

// divIcon side-steps Leaflet's default marker icons (which load PNGs from a
// bundle path that breaks under most bundlers). Same approach as Track's
// FlightTracker.jsx and the AircraftMediaPreview component.
const airportIcon = L.divIcon({
  html:
    '<div style="width:12px;height:12px;border-radius:50%;background:#0C3E71;border:2px solid white;box-shadow:0 1px 3px rgba(0,0,0,0.3)"></div>',
  className: '',
  iconSize: [12, 12],
  iconAnchor: [6, 6],
});

/**
 * Render a small off-screen Leaflet map with markers for each airport, wait
 * for tiles to load, then capture it via html2canvas as a base64 JPEG.
 *
 * Mirrors the mobile app's approach (react-native-maps + ViewShot.capture at
 * 400x300, quality 0.6). The web can't rasterise a native MapView, so this
 * substitutes an OSM-backed Leaflet map + html2canvas.
 *
 * IMPORTANT: html2canvas is called with useCORS:true and allowTaint:false.
 * The Carto tile CDN returns Access-Control-Allow-Origin:* so tiles load
 * cleanly, but if we set allowTaint:true, any external image would taint
 * the output canvas and `toDataURL` would throw a SecurityError. Keeping
 * taint off means we get a usable data URL back.
 *
 * @param {Array<{lat:number, lng:number, icao?:string}>} airports
 * @param {{width?:number, height?:number, quality?:number}} opts
 * @returns {Promise<string|null>} `data:image/jpeg;base64,...` URL, or null on failure.
 */
export async function captureRouteMap(airports, { width = 400, height = 300 } = {}) {
  const valid = (airports || []).filter(
    (a) => typeof a?.lat === 'number' && typeof a?.lng === 'number'
  );
  if (valid.length === 0) return null;

  const container = document.createElement('div');
  container.style.position = 'absolute';
  container.style.left = '-9999px';
  container.style.top = '0';
  container.style.width = `${width}px`;
  container.style.height = `${height}px`;
  document.body.appendChild(container);

  let map = null;
  try {
    map = L.map(container, {
      zoomControl: false,
      attributionControl: false,
      dragging: false,
      touchZoom: false,
      scrollWheelZoom: false,
      doubleClickZoom: false,
      boxZoom: false,
      keyboard: false,
    });

    // Esri World Light Gray Canvas — matches the drawer preview
    // (AircraftMediaPreview.jsx) and the Track page (FlightTracker.jsx)
    // so all three surfaces read the same. Swapped from CARTO Positron
    // because CARTO now requires an API key for its public tiles.
    // Access-Control-Allow-Origin:* on the Esri endpoint so tiles
    // cross-load without tainting; crossOrigin flag ensures the <img>
    // tags get a proper crossorigin attribute, which is what
    // html2canvas needs to draw them.
    //
    // NOTE: unlike CARTO's URL, the Esri tile template has no `{r}`
    // retina modifier — Leaflet's detectRetina flag is a no-op on
    // this service. The PDF map card may read slightly softer than
    // before; if that becomes a problem, bump the zoom by one level
    // in the setView / fitBounds calls below to fetch a denser
    // patch of tiles for the same coverage area.
    const tiles = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}', {
      maxZoom: 19,
      crossOrigin: true,
    }).addTo(map);

    valid.forEach((a) => {
      L.marker([a.lat, a.lng], { icon: airportIcon, title: a.icao || '' }).addTo(map);
    });

    if (valid.length === 1) {
      map.setView([valid[0].lat, valid[0].lng], 8, { animate: false });
    } else {
      const bounds = L.latLngBounds(valid.map((a) => [a.lat, a.lng]));
      // animate:false — jump straight to the fit view so tiles for the
      // FINAL view load once. With animation, `tiles.on('load')` fires for
      // the intermediate zoom too, and we can snapshot before the final
      // tiles land → tiles show through as the container background.
      //
      // padding:[20,20] — enough room for the pin's outer radius + a small
      // margin so the pin doesn't get clipped at the container edge, while
      // still keeping pins near the edges (not centred).
      map.fitBounds(bounds, { padding: [20, 20], animate: false });
    }

    // Wait for the tile layer's own "load" event — Leaflet fires it once
    // every tile in the current viewport has finished loading. That's the
    // most reliable signal that the map is ready to snapshot. Cap at 5s so
    // a stuck tile can't hang the PDF flow.
    await new Promise((resolve) => {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        resolve();
      };
      tiles.on('load', finish);
      // Fallback: even if the load event never fires (rare, e.g. a caching
      // proxy that serves everything instantly and fires load before we
      // attached the listener), wait a bit and proceed.
      setTimeout(finish, 5000);
    });

    // One more animation frame so Leaflet's final DOM writes are flushed.
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

    const canvas = await html2canvas(container, {
      useCORS: true,
      allowTaint: false,
      // Pure white backdrop. Carto light_all tiles have subtle transparency
      // in water regions; if the backdrop matches the card colour (#F0F4F8),
      // that pale grey bleeds through the tiles and reads as a milky
      // "overlay". White backdrop lets the tile land colours render at full
      // contrast, and any water tint stays clean.
      backgroundColor: '#FFFFFF',
      logging: false,
      width,
      height,
      // scale:2 captures at 2× resolution (800×600 for the default 400×300
      // container). PDF renders the media card at ~190×120 render size, so
      // supersampling here gives a crisp result after downscale.
      scale: 2,
    });

    // JPEG at quality 0.95 — @react-pdf/renderer's PNG codec (pdfkit)
    // downsamples large PNGs by simple averaging, which washes maps out
    // to a milky look. JPEG at high quality uses DCT which preserves
    // edges and contrast much better under downsampling, and since we
    // rendered white behind the tiles above there's no transparency to
    // lose in the JPEG round-trip.
    return canvas.toDataURL('image/jpeg', 0.95);
  } catch (err) {
    console.warn('captureRouteMap failed:', err);
    return null;
  } finally {
    try { map?.remove(); } catch { /* ignore */ }
    try { document.body.removeChild(container); } catch { /* ignore */ }
  }
}
